<script setup lang="ts">
import { computed, ref, type PropType, toRef } from 'vue';
import Dialog from 'primevue/dialog';
import {
  FIELD_TYPES,
  type FormField,
  FormFieldEffects
} from '@/models';
import { useFormBuilder } from '@/stores/formBuilder';
import { ButtonType, CustomButton, EffectPanel, setupEffects, getConditionsArray, type Effects } from '@/components';
import { cloneDeep } from 'lodash';
import InputText from 'primevue/inputtext';

const builder = useFormBuilder();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  formField: {
    type: Object as PropType<FormField>,
    required: true
  }
});
const localField = toRef<FormField>(props.formField);
const variableName = ref(props.formField.variableName);

const emits = defineEmits(['onClose', 'updateField']);

const closeDialog = () => {
  emits('onClose');
};

const fieldType = computed(() =>
  FIELD_TYPES.find(type => type.type === props.formField.type)
);

const initialEffects = ref<Effects>(setupEffects(cloneDeep(props.formField), builder.form!.items!));
const initialVariableName = ref(props.formField.variableName);
const effects = ref<Effects>(setupEffects(props.formField, builder.form!.items!));
const isPristine = computed(() => JSON.stringify(initialEffects.value) !== JSON.stringify(effects.value));
const isVariableNamePristine = ref(true);
const variableNameError = ref('');
const variableNameRegex = /^[a-zA-Z0-9_]*$/;

const onVariableNameChange = () => {
  isVariableNamePristine.value = initialVariableName.value === variableName.value;
  if (variableName.value && variableName.value.length > 0 && !variableNameRegex.test(variableName.value)) {
    variableNameError.value = 'Variable name can only contain letters, numbers, and underscores.';
  } else {
    variableNameError.value = '';
  }
};

const saveEffects = () => {
  localField.value.conditions = getConditionsArray(effects.value, localField.value);
  localField.value.variableName = variableName.value;
  closeDialog();
};
</script>

<template>
  <div class="dialog-root">
    <Dialog :visible="visible"
      modal
      class="dialog"
      append-to="self"
      @update:visible="closeDialog">
      <template #header>
        <div class="dialog-header">
            <div class="field-type">
                <FontAwesomeIcon :icon="[fieldType?.iconStyle, fieldType?.icon]" class="icon" />
                <div class="field-type-label">{{ formField.type }}</div>
            </div>
            <div class="field-label">{{ formField.label }}</div>
        </div>
      </template>
      <div class="content-root">
        <InputText v-model="variableName"
          class="variable-name-input"
          @input="onVariableNameChange"
          placeholder="Enter a unique field variable name"/>
        <div class="alert error" v-if="variableNameError">
          <FontAwesomeIcon :icon="['far', 'circle-exclamation']" class="icon" />
          <div class="label">{{ variableNameError }}</div>
        </div>
        <EffectPanel :effect="effects.Show" :effect-type="FormFieldEffects.SHOW"/>
        <EffectPanel :effect="effects.Valid" :effect-type="FormFieldEffects.VALID"/>
        <EffectPanel :effect="effects.Warn" :effect-type="FormFieldEffects.WARN"/>
        <EffectPanel :effect="effects.Disable" :effect-type="FormFieldEffects.DISABLE"/>
        <EffectPanel :effect="effects.Required" :effect-type="FormFieldEffects.REQUIRED"/>
        <EffectPanel :effect="effects.Default" :effect-type="FormFieldEffects.DEFAULT"/>
      </div>
      <div class="footer">
        <div class="empty"></div>
        <div class="footer-buttons">
          <CustomButton text="Cancel"
            @onClick="closeDialog"
            :margin-left="16"
            :type="ButtonType.OUTLINED"/>
          <CustomButton text="Save"
            @onClick="saveEffects"
            :disabled="!isPristine && isVariableNamePristine"
            :margin-left="16"
            :type="ButtonType.POSITIVE"/>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
@use "../../../../assets/dialog" as *;
:deep(.p-dialog) {
  height: 80vh;
  min-width: 1000px;
}
:deep(.p-dialog-header) {
  padding: unset;
  padding: 40px 40px 0px 40px;
}
:deep(.p-dialog-content) {
  padding: unset;
}
.field-type {
    display: flex;
    align-items: center;

    .icon {
        font-size: 16px;
    }

    .field-type-label {
        margin-left: 16px;
        font-size: 16px;
        font-weight: 700;
    }
}
.field-label {
    font-weight: 700;
    font-size: 20px;
}
.content-root {
    padding: 20px 40px 40px 40px;
    margin-bottom: 40px;
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 9999;

  .footer-buttons {
    display: flex;
  }
}
.error {
  padding: 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  color: #5c0a11;
  border: 1px solid #ea3546;
  background-color: #fef5f6;
  margin-bottom: 8px;

  .icon {
    color: #ea3546;
    font-size: 16px;
    margin-right: 8px;
  }
}
.variable-name-input {
  width: 100%;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 8px;
}
</style>
